/**
 * RegrasAplicadasPanel — painel "Regras aplicadas" da ficha da visita (ADR-030, Req. 1.6).
 *
 * Uma linha por achado de todos os campos: nível, `RN-xx · <rótulo do campo>` e a
 * mensagem, em ordem de severidade (ALERTA → CORRIGIDO → OBSERVACAO → OK), com os totais
 * "N alertas · N correções" e, à parte, "N observações" (ADR-035: observação não é alerta).
 * Recolhível (fechado de início em tela estreita).
 * Visita sem achados gravados (anterior às explicações) → o painel não aparece.
 * ADR-048: o achado que cita uma evidência complementar termina com "ver evidência".
 */

import { AchadoCampo, CampoFicha, NivelAchado } from '../services/consulta-types';
import { rotuloCampo } from '../services/rotulos-campos';
import { MarcadorPill } from './ConsultaShared';
import { CLASSE_NIVEL } from './AchadosCampo';
import { LinkVerEvidencia } from './LinkVerEvidencia';

const ORDEM: Readonly<Record<NivelAchado, number>> = { ERRO: 0, ALERTA: 1, CORRIGIDO: 2, OBSERVACAO: 3, OK: 4 };

export interface LinhaRegra extends AchadoCampo {
  readonly campo: string;
}

/** Achados de todos os campos, do mais grave ao OK (estável dentro do mesmo nível). */
export function linhasRegras(campos: readonly CampoFicha[]): LinhaRegra[] {
  return campos
    .flatMap((c) => (c.achados ?? []).map((a) => ({ ...a, campo: c.nomeCampo })))
    .map((l, i) => ({ l, i }))
    .sort((a, b) => ORDEM[a.l.nivel] - ORDEM[b.l.nivel] || a.i - b.i)
    .map(({ l }) => l);
}

function plural(n: number, um: string, varios: string): string {
  return `${n} ${n === 1 ? um : varios}`;
}

function telaEstreita(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(max-width: 760px)').matches
    : false;
}

export function RegrasAplicadasPanel({ campos }: { readonly campos: readonly CampoFicha[] }): JSX.Element | null {
  const linhas = linhasRegras(campos);
  if (linhas.length === 0) {
    return null;
  }
  const alertas = linhas.filter((l) => l.nivel === 'ALERTA' || l.nivel === 'ERRO').length;
  const correcoes = linhas.filter((l) => l.nivel === 'CORRIGIDO').length;
  const observacoes = linhas.filter((l) => l.nivel === 'OBSERVACAO').length;
  return (
    <section className="card regras-card">
      <details className="regras" open={!telaEstreita()}>
        <summary>
          <h2>Regras aplicadas</h2>
          <span className="regras-contagens">
            <span className="regras-totais" data-testid="regras-totais">
              {plural(alertas, 'alerta', 'alertas')} · {plural(correcoes, 'correção', 'correções')}
            </span>
            {observacoes > 0 && (
              <span className="regras-observacoes" data-testid="regras-observacoes">
                {plural(observacoes, 'observação', 'observações')}
              </span>
            )}
          </span>
        </summary>
        <ul className="regras-lista">
          {linhas.map((l, i) => (
            <li key={`${l.campo}-${l.regra}-${i}`} className="regra-linha" data-nivel={l.nivel}>
              <span className="regra-nivel">
                <MarcadorPill marcador={l.nivel} />
              </span>
              <span className="regra-id">
                {l.regra} · {rotuloCampo(l.campo)}
              </span>
              <span className={`regra-msg ${CLASSE_NIVEL[l.nivel]}`}>
                {l.mensagem}
                <LinkVerEvidencia evidenciaId={l.evidenciaId} />
              </span>
            </li>
          ))}
        </ul>
      </details>
    </section>
  );
}
